import { HttpStatusCode } from "axios";
import { EmployeeSettings } from "../../models/employee-settings";
import { SystemPermissions } from "../../models/permission";
import { System, ISystem } from "../../models/system";
import { IUser, User } from "../../models/user";
import { getAsync, getMultiple, putAsync, deleteAsync } from "./api-base";
import { CompanyProperties, IPayrollDataValue } from "../../models/company-properties";
import config from "../../config/config";

export type IsoCountry = {
	isoCountryCode: string;
	description: string;
};

type BffUserClaim = {
	type: string;
	value: string | number;
	valueType: string | null;
};

export type BffUserData = BffUserClaim[];

export const getSystems = async (): Promise<System[]> => {
	const systems = await getAsync<ISystem[]>("myPages/systems");
	return systems.map((system) => new System(system));
};

export const getAllSystems = async (): Promise<System[]> => {
	const systems = await getAsync<ISystem[]>("systems/me");
	return systems.map((system) => new System(system));
};

export const getUser = async () => {
	const user = await getAsync<IUser>("management/users/me");
	return new User(user);
};

export const getAllUserForSystem = async (system: string) => {
	return await getAsync<IUser[]>("management/users/currentsystem", system, { system });
};

export const getPermissions = async (systems: System[]) => {
	return await getMultiple(
		systems,
		"permissions/me",
		(permissionValues: SystemPermissions) => new SystemPermissions(permissionValues),
	);
};

export const getCompanyProperties = async (systems: System[]) => {
	return await getMultiple(
		systems,
		"companyInformation/myproperties",
		(properties: Record<string, IPayrollDataValue>) => new CompanyProperties(properties),
	);
};

export const updateUserAsync = async (user: Partial<User>) => {
	return await putAsync<HttpStatusCode, Partial<User>>("myPages/updateMe", user, undefined);
};

export const haveActivePushToken = async (token: string): Promise<boolean> => {
	return await getAsync<boolean>(`management/users/pushToken?token=${token}&application=2`);
};

export const registerPushtokenAsync = async (pushToken: string, platform: string) => {
	return await putAsync<boolean, undefined>(
		`management/users/pushToken?token=${pushToken}&platform=${platform.toLowerCase()}&application=2`,
	);
};

export const unregisterPushNotificationAsync = async () => {
	return await deleteAsync<boolean>("management/users/pushToken?application=2");
};

export const getEmploymentSettings = async (system: string) => {
	const data = await getAsync<EmployeeSettings>("EmployeeSettings/getMySettings", system, { system });
	return new EmployeeSettings(data, system);
};

export const syncConsent = async (consent: boolean, system: string) =>
	await putAsync<unknown, undefined>(`myPages/syncConsent?consent=${consent}`, undefined, system);

export const getIsoCountries = async (): Promise<[IsoCountry]> => {
	const data = await getAsync<[IsoCountry]>("isoCountries");
	const sortedData = data.sort((a: IsoCountry, b: IsoCountry) => a.description.localeCompare(b.description));
	return sortedData;
};

export const getBffUser = async (): Promise<BffUserData> => {
	const url = `${config.bffUrl}/user`;
	return await getAsync<BffUserData>(url);
};
